// src/components/auth/TermsAndPrivacy.jsx
// ==============================================
// Renders the content for a Terms of Service and Privacy Policy.
// This component can be used within a modal or on a standalone page.
// The `onClose` prop is provided by the parent to handle closing behavior.
// ==============================================

import React from 'react';

const KeyPointsSummary = () => (
    <div className="p-4 mb-4 border border-zinc-700 rounded-lg bg-zinc-800/50">
      <h4 className="font-bold text-white mb-2">Key Points in Plain English</h4>
      <ul className="list-disc list-inside space-y-2 text-sm text-zinc-400">
        <li><strong>We NEVER log your prompts.</strong> The content of your conversations is private. Period.</li>
        <li><strong>We are a platform.</strong> When you connect to third-party services (like AI models or knowledge banks), their terms and privacy policies apply to your interaction with them.</li>
        <li><strong>Your data is secure.</strong> Sensitive information like your credentials for other services is always stored encrypted at rest on a SOC 2 compliant cloud architecture.</li>
        <li><strong>We track usage, not users.</strong> We collect anonymized, aggregated data about which features and models are used to improve our service. We do not track individual user activity.</li>
        <li><strong>Risk-free trial and refunds.</strong> We offer a 3-day free trial (no credit card required) and a 30-day, no-hassle refund policy on paid plans. Just email us: <strong>support [at] shelbula [dot] com</strong>.</li>
      </ul>
    </div>
);

export const TermsAndPrivacy = ({ onClose, initialTab = 'terms' }) => {
  const [activeTab, setActiveTab] = React.useState(initialTab);

  React.useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const baseButtonClass = "px-4 py-1.5 text-sm font-medium rounded-md transition-colors";
  const activeClass = "bg-blue-600 text-white";
  const inactiveClass = "text-zinc-400 hover:bg-zinc-700";

  return (
    <div className="bg-zinc-900 text-zinc-300 p-6 rounded-lg max-w-3xl w-full">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-white">Terms and Privacy</h2>
      </div>
      
      <div className="border-b border-zinc-700">
        <div className="p-2 flex space-x-2">
          <button
            className={`${baseButtonClass} ${activeTab === 'terms' ? activeClass : inactiveClass}`}
            onClick={() => setActiveTab('terms')}
          >
            Terms of Service
          </button>
          <button
            className={`${baseButtonClass} ${activeTab === 'privacy' ? activeClass : inactiveClass}`}
            onClick={() => setActiveTab('privacy')}
          >
            Privacy Policy
          </button>
        </div>
      </div>

      <div className="prose prose-sm prose-invert max-w-none text-zinc-400 h-[60vh] overflow-y-auto p-4 custom-scrollbar">
        {activeTab === 'terms' ? (
          <>
            <KeyPointsSummary />
            <h2>Terms of Service</h2>
            <p>Last updated: June 13, 2024</p>
            
            <h3>1. Introduction & Agreement</h3>
            <p>Welcome to Shelbula! Shelbula.com ("we," "us," or "our") provides a universal AI chat user interface (the "Service"). By accessing and using our Service, you agree to these Terms of Service (the "Terms").</p>
            
            <h3>2. Payments, Subscriptions, and Refunds</h3>
            <p>All payments for subscriptions are processed through our third-party payment processor, Stripe. By providing a payment method, you agree to the recurring billing of subscription fees. Your subscription will automatically renew at the end of each billing cycle unless you cancel it.</p>
            <p><strong>Refund Guarantee:</strong> We offer a 3-day free trial, no credit card required. For paid subscriptions, we offer a 30-day, no-hassle, money-back guarantee. If you are not satisfied, request a full refund within 30 days of your initial payment by contacting our support team at: <strong>support [at] shelbula [dot] com</strong>.</p>
            
            <h3>3. Third-Party Services and Integrations</h3>
            <p>Our Service acts as a platform to connect with various third-party AI models, services, and Multi-Capability Providers ("MCPs"). When you connect to or use these third-party services through Shelbula, your use is governed by their respective terms of service and privacy policies. We are not responsible for the practices of these third-party services.</p>
            <p>The "Knowledge Bank" and long-term memory features are provided through an integration with <strong>RememberAPI.com</strong>. Your use of these specific features is subject to the terms and policies of RememberAPI.com.</p>
            
            <h3>4. Acceptable Use Policy</h3>
            <p>You agree not to use the Service for any unlawful or prohibited activities, including but not limited to: distributing malware, engaging in harassment, violating intellectual property rights, or attempting to gain unauthorized access to our systems.</p>

            <h3>5. Intellectual Property</h3>
            <p>We claim no ownership of the content you generate ("User Content"). You retain all rights to your User Content. We retain all ownership rights to the Shelbula brand, logos, and the user interface of the Service itself.</p>

            <h3>6. Termination</h3>
            <p>We may suspend or terminate your access to the Service for a material breach of these Terms.</p>

            <h3>7. Disclaimer of Warranties & Limitation of Liability</h3>
            <p>The Service is provided "as is" without warranties of any kind. We are not liable for any indirect, incidental, or consequential damages arising out of your use of the Service.</p>
            
            <h3>8. Governing Law</h3>
            <p>These Terms shall be governed by the laws of the State of Florida, United States.</p>
          </>
        ) : (
          <>
            <KeyPointsSummary />
            <h2>Privacy Policy</h2>
            <p>Last updated: June 13, 2024</p>
            
            <h3>1. Our Commitment to Your Privacy</h3>
            <p>This Privacy Policy explains how we collect, use, and protect your data. Our guiding principle is to collect only what is necessary to provide an excellent service while prioritizing your privacy.</p>
            
            <h3>2. Data We Collect and Why</h3>
            <ul>
                <li><strong>Account Information:</strong> Your email address for login, managed via Google Firebase Authentication.</li>
                <li><strong>User Preferences & Credentials:</strong> We securely store your settings (like theme) and credentials for third-party services (API keys, etc.) to provide a seamless experience. All such credentials are fully encrypted at rest.</li>
                <li><strong>Anonymized Usage Data:</strong> We collect aggregated, anonymous data about feature and model usage (e.g., "Feature X was used 500 times today"). This helps us improve the service without knowing anything about you or your content.</li>
            </ul>
            
            <h3>3. What We Explicitly DO NOT Collect</h3>
            <p><strong>We have a universal NO PROMPT LOGGING policy.</strong> The content of your conversations—prompts and responses—is never stored on our servers or seen by us. This applies to all users, free and paid.</p>

            <h3>4. Data Storage and Security</h3>
            <p>Security is paramount. Our services are hosted on Google Cloud Platform (GCP), a SOC 2 compliant architecture. All sensitive data we store, such as your credentials for other services, is encrypted at rest using industry-best standards.</p>

            <h3>5. Third-Party Data Processors</h3>
            <p>We use trusted third parties to provide our Service. They are contractually obligated to protect your data.</p>
            <ul>
              <li><strong>Google Firebase:</strong> For user authentication and database management.</li>
              <li><strong>Google Analytics:</strong> For analyzing anonymized website traffic.</li>
              <li><strong>Stripe:</strong> For secure payment processing.</li>
              <li><strong>RememberAPI.com:</strong> Powers the Knowledge Bank and long-term memory features. Your use of these features means data is processed according to their privacy policy.</li>
            </ul>

            <h3>6. Your Data Protection Rights (GDPR & CCPA)</h3>
            <p>You have the right to access, update, delete, or restrict the processing of your personal information. To exercise these rights, please contact us.</p>

            <h3>7. Contact Us</h3>
            <p>For any questions about this Privacy Policy, please email us at: <strong>privacy [at] shelbula [dot] com</strong>.</p>
          </>
        )}
      </div>

      <div className="flex justify-end pt-4 mt-4 border-t border-zinc-700">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium h-10 px-4 py-2 bg-zinc-700 text-zinc-100 hover:bg-zinc-600"
        >
          Close
        </button>
      </div>
    </div>
  );
};